import CurrencyInput from 'react-currency-input-field'
import Select from 'react-select'

import BrandsFilterDropdown from '@Utils/inputs/searchable-dropdown/brands/filter'
import CategoriesFilterDropdown from '@Utils/inputs/searchable-dropdown/categories/filter'
import CompanyDropdown from '@Utils/inputs/searchable-dropdown/company'
import filterConditionGreenStyles from '@Utils/inputs/searchable-dropdown/filterConditionGreenStyles'
import filterConditionStyles from '@Utils/inputs/searchable-dropdown/filterConditionStyles'
import PositionsFilterDropdown from '@Utils/inputs/searchable-dropdown/positions/filter'
import TagsDropdown from '@Utils/inputs/searchable-dropdown/tags'

import type { ValueType } from './types'

type Props = {
  valueType: ValueType
  value: string | [string, string]
  onChange: (value: string | [string, string]) => void
  onSubmit: () => void
  filterOperator?: string
  onFilterOperatorChange?: (value: string | undefined) => void
}

type Option = { label: string; value: string }

const mapMulti = (opts: any[]): string =>
  opts?.map((o) => (o.isNegative ? o.value * -1 : o.value)).join(',') ?? ''

// Lets users type either "." or "," as the decimal separator.
const dotToComma = (raw: string) => raw.replace(/\./g, ',')

const inputClass =
  'border border-gray-300 rounded text-sm px-2.5 py-1.5 w-full focus:outline-none focus:border-blue-400 bg-white'

function SimpleSelect({
  options,
  value,
  onChange,
}: {
  options: Option[]
  value: string
  onChange: (v: string) => void
}) {
  return (
    <Select
      options={options}
      value={options.find((o) => o.value === value) ?? null}
      onChange={(opt) => onChange((opt as Option | null)?.value ?? '')}
      styles={filterConditionStyles}
      isSearchable={false}
      isClearable={false}
      placeholder="Select..."
    />
  )
}

export default function ValueInput({
  valueType,
  value,
  onChange,
  onSubmit,
  filterOperator,
  onFilterOperatorChange,
}: Props) {
  const str = typeof value === 'string' ? value : ''
  const range = Array.isArray(value)
    ? (value as [string, string])
    : (['', ''] as [string, string])

  switch (valueType) {
    case 'text':
      return (
        <input
          type="text"
          className={inputClass}
          value={str}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
        />
      )

    case 'multi-category':
      return (
        <CategoriesFilterDropdown
          isMulti={true}
          filterValues={str || undefined}
          onChange={(opts: any) => onChange(mapMulti(opts))}
          onSubmit={onSubmit}
        />
      )

    case 'multi-brand':
      return (
        <BrandsFilterDropdown
          isMulti={true}
          filterValues={str || undefined}
          onChange={(opts: any) => onChange(mapMulti(opts))}
          onSubmit={onSubmit}
        />
      )

    case 'multi-tag':
      return (
        <TagsDropdown
          isCreatable={false}
          isMulti={true}
          customComponent={true}
          filterValues={str || undefined}
          onChange={(opts: any) => onChange(mapMulti(opts))}
          onSubmit={onSubmit}
          separator={filterOperator}
          setSeparator={onFilterOperatorChange}
        />
      )

    case 'multi-company':
      return (
        <CompanyDropdown
          canBeNegative={true}
          isCreatable={false}
          customComponent={true}
          isMulti={true}
          filterValues={str || undefined}
          onChange={(opts: any) => onChange(mapMulti(opts))}
          onSubmit={onSubmit}
          customStyles={filterConditionGreenStyles}
          separator={filterOperator}
          setSeparator={onFilterOperatorChange}
        />
      )

    case 'multi-position':
      return (
        <PositionsFilterDropdown
          isMulti={true}
          defaultValue={str || undefined}
          onChange={(opts: any) => {
            const v =
              opts
                ?.map((o: any) => ({ ...o, value: Number(o.value) }))
                .map((o: any) => (o.isNegative ? o.value * -1 : o.value))
                .join(',') ?? ''
            onChange(v)
          }}
          onSubmit={onSubmit}
        />
      )

    case 'number-range':
      return (
        <div className="flex items-center gap-1.5 min-w-0">
          <CurrencyInput
            className="border border-gray-300 rounded text-sm px-2.5 py-1.5 min-w-0 w-full focus:outline-none"
            allowNegativeValue={false}
            decimalScale={2}
            disableGroupSeparators
            groupSeparator="."
            decimalSeparator=","
            transformRawValue={dotToComma}
            placeholder="Min"
            value={range[0]}
            onValueChange={(v) => onChange([v ?? '', range[1]])}
            onKeyDown={(e: any) => e.key === 'Enter' && onSubmit()}
          />
          <span className="text-gray-400 text-sm shrink-0">—</span>
          <CurrencyInput
            className="border border-gray-300 rounded text-sm px-2.5 py-1.5 min-w-0 w-full focus:outline-none"
            allowNegativeValue={false}
            decimalScale={2}
            disableGroupSeparators
            groupSeparator="."
            decimalSeparator=","
            transformRawValue={dotToComma}
            placeholder="Max"
            value={range[1]}
            onValueChange={(v) => onChange([range[0], v ?? ''])}
            onKeyDown={(e: any) => e.key === 'Enter' && onSubmit()}
          />
        </div>
      )

    case 'select-change':
      return (
        <SimpleSelect
          options={[
            { label: 'Increased', value: '1' },
            { label: 'Decreased', value: '0' },
          ]}
          value={str}
          onChange={(v) => onChange(v)}
        />
      )

    case 'select-stock':
      return (
        <SimpleSelect
          options={[
            { label: 'In stock', value: '1' },
            { label: 'Out of stock', value: '0' },
          ]}
          value={str}
          onChange={(v) => onChange(v)}
        />
      )

    case 'select-match':
      return (
        <SimpleSelect
          options={[
            { label: 'Matched', value: '0' },
            { label: 'Unmatched', value: '1' },
          ]}
          value={str}
          onChange={(v) => onChange(v)}
        />
      )

    case 'select-url-status':
      return (
        <SimpleSelect
          options={[
            { label: '404', value: 'Yes' },
            { label: 'No 404', value: 'No' },
          ]}
          value={str}
          onChange={(v) => onChange(v)}
        />
      )

    case 'select-favorites':
      return (
        <SimpleSelect
          options={[
            { label: 'Enabled', value: 'on' },
            { label: 'Disabled', value: 'off' },
          ]}
          value={str}
          onChange={(v) => onChange(v)}
        />
      )

    case 'static':
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded text-sm bg-gray-100 text-gray-600 font-medium">
          Enabled
        </span>
      )

    case 'none':
      return null

    default:
      return null
  }
}
